import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  makeStyles,
  IconButton,
  Button,
  Grid,
  Tooltip,
  CircularProgress
} from '@material-ui/core';
import moment from 'moment';
import { useSelector } from 'react-redux';

// Icons
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import PauseIcon from '@material-ui/icons/Pause';
import VideocamIcon from '@material-ui/icons/Videocam';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import CloseIcon from '@material-ui/icons/Close';
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import GetAppIcon from '@material-ui/icons/GetApp';
import RefreshIcon from '@material-ui/icons/Refresh';

import { useRecordingList } from '../../../sdk/livekit/hooks/useRecordingList';
import { useCardPlayback } from './playback/useCardPlayback';
import { PlaybackDeviceContext, usePlaybackDeviceProvider, preemptHintMessage } from './playback/playbackDeviceContext';
import { clipTimelineText, fmtClipMmss } from './playback/format';
import { resolveDecodeTier } from './playback/decode/tierSelect';

// 4G playback wire contract, cache-first storage check, per-card state
// machine, 3-tier H.265 decode, cushion/preemption behaviour: see
// docs/PLAYBACK-HANDOFF.md and ./playback/*. This file keeps the page
// SHELL the owner already approved (left filter panel in HeaderToolBar.js,
// the hour-grouped card grid below) and only replaces the player inside
// each card, per the owner's 2026-09-12 instruction.

function cameraNumFromMask(mask) {
  return ((mask ?? 1) & 2) && !((mask ?? 1) & 1) ? 2 : 1;
}

function camLabelFromMask(mask) {
  return ((mask ?? 1) & 2) && !((mask ?? 1) & 1) ? 'CAM 2' : 'CAM 1';
}

function fmtDuration(ms) {
  if (!ms) return '0s';
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
}

const useStyles = makeStyles(() => ({
  root: {
    padding: '24px 30px',
    height: 'calc(100vh - 66px)',
    overflowY: 'auto',
    backgroundColor: '#ffffff',
    color: '#12202f',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#e4e6eb',
      borderRadius: '4px',
    },
  },
  hourSection: {
    marginBottom: '32px',
  },
  hourHeader: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#0066cc',
    borderBottom: '1px solid #e4e6eb',
    paddingBottom: '8px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  clipCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e4e6eb',
    borderRadius: '10px',
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
    '&:hover': {
      borderColor: '#0066cc',
      boxShadow: '0 8px 24px rgba(0, 102, 204, 0.12)',
      transform: 'translateY(-2px)',
    },
  },
  videoArea: {
    height: '140px',
    position: 'relative',
    backgroundColor: '#000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    overflow: 'hidden',
    // Native Fullscreen API target (⛶ button) — no second decoder instance,
    // no second <video>: the SAME element just fills the screen.
    '&:fullscreen': {
      height: '100vh',
    },
  },
  playOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3,
  },
  cardTimeBadge: {
    position: 'absolute',
    top: '8px',
    left: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    color: '#12202f',
    fontSize: '11px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '4px',
    zIndex: 2,
    border: '1px solid #e4e6eb',
  },
  cardDurationBadge: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    color: '#0066cc',
    fontSize: '11px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '4px',
    zIndex: 2,
    border: '1px solid #e4e6eb',
  },
  inlineTimeBadge: {
    backgroundColor: '#f0f2f5',
    color: '#12202f',
    fontSize: '11px',
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: '4px',
    border: '1px solid #e4e6eb',
    whiteSpace: 'nowrap',
  },
  inlineDurationBadge: {
    backgroundColor: '#e6f0fa',
    color: '#0066cc',
    fontSize: '11px',
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: '4px',
    border: '1px solid #b3d1ff',
    whiteSpace: 'nowrap',
  },
  fullscreenOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '16px 24px',
    background: 'linear-gradient(180deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0) 20%, rgba(0,0,0,0) 70%, rgba(0,0,0,0.85) 100%)',
    transition: 'opacity 0.3s ease',
  },
  cardDetails: {
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ffffff',
  },
  clipTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#12202f',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  clipSub: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '11px',
    color: '#65676b',
    marginTop: '2px',
  },
  // Clip timeline (§7.4): fill = bufferedSeconds / durationMs, never bytes.
  timelineTrack: {
    position: 'relative',
    height: '6px',
    borderRadius: '3px',
    backgroundColor: '#e4e6eb',
    marginTop: '8px',
    overflow: 'visible',
  },
  timelineBuffer: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    borderRadius: '3px',
    backgroundColor: '#b3d1ff',
    transition: 'width 0.2s linear',
  },
  timelineFill: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    borderRadius: '3px',
    backgroundColor: '#0066cc',
    transition: 'width 0.2s linear',
    zIndex: 1,
  },
  timelineThumb: {
    position: 'absolute',
    top: '50%',
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#0066cc',
    border: '2px solid #ffffff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
    transform: 'translate(-50%, -50%)',
    zIndex: 3,
    transition: 'left 0.1s linear',
  },
  timelineTick: {
    position: 'absolute',
    top: '-2px',
    width: '2px',
    height: '10px',
    backgroundColor: '#c4c9d1',
    zIndex: 2,
    '&.lit': {
      backgroundColor: '#0066cc',
    },
  },
  timelineText: {
    fontSize: '10px',
    color: '#65676b',
    marginTop: '4px',
  },
  cardControls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '8px',
    borderTop: '1px solid #e4e6eb',
    paddingTop: '6px',
  },
  iconBtn: {
    color: '#65676b',
    padding: '4px',
    margin: '0 2px',
    transition: 'color 0.2s, background-color 0.2s',
    '&:hover': {
      color: '#0066cc',
      backgroundColor: 'rgba(0, 102, 204, 0.08)',
    },
    '&.Mui-disabled': {
      backgroundColor: 'transparent',
      color: '#c4c9d1',
    },
  },
  startBtn: {
    fontSize: '11px',
    fontWeight: 700,
    padding: '2px 10px',
    minWidth: 0,
    textTransform: 'none',
  },
}));

/**
 * The clip timeline (§7.4): buffer bar (bufferedSec) + active playhead bar
 * (currentTimeSec, "chạy đến đâu xanh đến đấy") + interactive click-to-seek,
 * with minute ticks.
 */
function ClipTimeline({ classes, currentTimeSec, bufferedSec, durationMs, onSeek, disabled }) {
  const durationSec = (durationMs || 0) / 1000;
  const playPct = durationSec > 0 ? Math.min(100, ((currentTimeSec || 0) / durationSec) * 100) : 0;
  const bufferPct = durationSec > 0 ? Math.min(100, ((bufferedSec || 0) / durationSec) * 100) : 0;
  const ticks = [];
  if (durationSec > 0) {
    for (let sec = 60; sec < durationSec; sec += 60) {
      ticks.push({ sec, lit: (currentTimeSec || 0) >= sec });
    }
  }

  const handleTrackClick = (e) => {
    if (disabled || !onSeek || !durationSec) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const pct = Math.max(0, Math.min(1, clickX / rect.width));
    onSeek(pct * durationSec);
  };

  return (
    <Box>
      <Box
        className={classes.timelineTrack}
        onClick={handleTrackClick}
        style={{ cursor: disabled || !durationSec ? 'default' : 'pointer' }}
      >
        <Box className={classes.timelineBuffer} style={{ width: `${bufferPct}%` }} />
        <Box className={classes.timelineFill} style={{ width: `${playPct}%` }} />
        {ticks.map((t) => (
          <span
            key={t.sec}
            className={`${classes.timelineTick}${t.lit ? ' lit' : ''}`}
            style={{ left: `${(t.sec / durationSec) * 100}%` }}
          />
        ))}
        {playPct > 0 && (
          <span
            className={classes.timelineThumb}
            style={{ left: `${playPct}%` }}
          />
        )}
      </Box>
      <Typography className={classes.timelineText}>
        {clipTimelineText(bufferedSec, durationMs)}
      </Typography>
    </Box>
  );
}

const ClipCard = ({ clip, deviceSerial, classes }) => {
  const pb = useCardPlayback({
    deviceSerial,
    cameraNum: clip.cameraNum,
    camIndex0: clip.cameraNum - 1, // wire is 1-based, storage listing is 0-based (§4.1/trap#2)
    slotId: clip.slot_id,
    tsUs: clip.ts_us,
    durationMs: clip.durationMs,
  });

  const noSlot = clip.slot_id == null;
  const overlayTooltip = pb.otherUploading ? preemptHintMessage(pb.otherUploading) : 'Phát đoạn này';
  const videoAreaRef = useRef(null);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [showBadges, setShowBadges] = useState(true);
  const controlsTimeoutRef = useRef(null);
  const badgeTimeoutRef = useRef(null);

  useEffect(() => {
    const handleFsChange = () => {
      const fs = document.fullscreenElement === videoAreaRef.current;
      setIsFullscreen(fs);
      if (fs) setControlsVisible(true);
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  // Initial auto-hide for badges after 3s on load
  useEffect(() => {
    badgeTimeoutRef.current = setTimeout(() => {
      setShowBadges(false);
    }, 3000);
    return () => {
      if (badgeTimeoutRef.current) clearTimeout(badgeTimeoutRef.current);
    };
  }, []);

  const handleVideoAreaMouseMove = useCallback(() => {
    setShowBadges(true);
    if (badgeTimeoutRef.current) clearTimeout(badgeTimeoutRef.current);
    badgeTimeoutRef.current = setTimeout(() => {
      setShowBadges(false);
    }, 2500);
  }, []);

  const handleVideoAreaMouseLeave = useCallback(() => {
    if (badgeTimeoutRef.current) clearTimeout(badgeTimeoutRef.current);
    badgeTimeoutRef.current = setTimeout(() => {
      setShowBadges(false);
    }, 400);
  }, []);

  const handleMouseMoveFullscreen = useCallback(() => {
    if (!isFullscreen) return;
    setControlsVisible(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => {
      setControlsVisible(false);
    }, 3500);
  }, [isFullscreen]);

  const handleExitFullscreen = useCallback((e) => {
    if (e) e.stopPropagation();
    if (document.exitFullscreen) {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  const handleFullscreen = useCallback((e) => {
    e.stopPropagation();
    const el = videoAreaRef.current;
    if (el && el.requestFullscreen) el.requestFullscreen().catch(() => {});
  }, []);

  const handleScreenshotClick = useCallback((e) => {
    e.stopPropagation();
    const dataUrl = pb.handleScreenshot();
    if (!dataUrl) return;
    const link = document.createElement('a');
    link.download = `chup_${clip.timestamp.replace(/:/g, '-')}.png`;
    link.href = dataUrl;
    link.click();
  }, [pb, clip.timestamp]);

  return (
    <Box className={classes.clipCard}>
      <Box
        ref={videoAreaRef}
        className={classes.videoArea}
        onClick={noSlot ? undefined : pb.handleVideoAreaClick}
        onMouseMove={(e) => {
          handleMouseMoveFullscreen();
          handleVideoAreaMouseMove();
        }}
        onMouseLeave={handleVideoAreaMouseLeave}
      >
        <span
          className={classes.cardTimeBadge}
          style={{
            opacity: isFullscreen ? 0 : (!pb.isPlaying ? 1 : (showBadges ? 1 : 0)),
            transition: 'opacity 0.4s ease',
            pointerEvents: 'none',
          }}
        >
          {clip.timestamp}
        </span>
        <span
          className={classes.cardDurationBadge}
          style={{
            opacity: isFullscreen ? 0 : (!pb.isPlaying ? 1 : (showBadges ? 1 : 0)),
            transition: 'opacity 0.4s ease',
            pointerEvents: 'none',
          }}
        >
          {clip.fileSize}
        </span>

        {/* All three decode-tier mount points always exist (handoff §8) —
            exactly one is visible at a time, chosen by decodePath, so refs
            are stable before the first playback_ack ever arrives. */}
        <video
          ref={pb.videoRef}
          playsInline
          hidden={pb.decodePath !== 'mse'}
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
        <canvas
          ref={pb.canvasRef}
          hidden={pb.decodePath !== 'webcodecs'}
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
        <div
          ref={pb.wasmContainerRef}
          id={`playback-wasm-${clip.id}`}
          hidden={pb.decodePath !== 'wasm'}
          style={{ width: '100%', height: '100%' }}
        />

        {!pb.everPlayable && (
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" position="absolute" style={{ inset: 0 }} gap={1}>
            {(pb.status === 'requesting' || pb.status === 'buffering') && (
              <>
                <CircularProgress size={22} style={{ color: '#58a6ff' }} />
                <Typography variant="caption" style={{ color: '#8b949e', fontSize: '11px', fontWeight: 600 }}>
                  {pb.note || 'Đang kết nối…'}
                </Typography>
              </>
            )}
          </Box>
        )}

        {/* Big one-shot ▶ (§7.1): only until first pressed, then gone for
            good — never dimmed/blocked for another card's transfer (§4.4
            point 6), only a tooltip naming the cost. */}
        {!pb.overlayDismissed && !isFullscreen && (
          <Tooltip title={noSlot ? 'Không có slot_id — không thể phát' : overlayTooltip}>
            <span className={classes.playOverlay}>
              <IconButton style={{ color: '#e57373', backgroundColor: 'transparent' }} disabled={noSlot}>
                <VideocamIcon style={{ fontSize: 40 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {/* Fullscreen Overlay Controls */}
        {isFullscreen && (
          <Box
            className={classes.fullscreenOverlay}
            style={{ opacity: controlsVisible ? 1 : 0, pointerEvents: controlsVisible ? 'auto' : 'none' }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography style={{ color: '#ffffff', fontSize: '14px', fontWeight: 600 }}>
                {clip.fileName}
              </Typography>
              <IconButton onClick={handleExitFullscreen} style={{ color: '#ffffff' }} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            <Box display="flex" flexDirection="column" gap="8px" style={{ width: '100%' }}>
              <ClipTimeline classes={classes} currentTimeSec={pb.currentTimeSec} bufferedSec={pb.bufferedSec} durationMs={pb.durationMs} onSeek={pb.handleSeek} />
              <Box display="flex" alignItems="center" justifyContent="space-between" style={{ width: '100%' }}>
                <Box display="flex" alignItems="center" gap={2}>
                  <IconButton onClick={pb.handlePlayToggle} style={{ color: '#ffffff' }} size="small">
                    {pb.isPlaying ? <PauseIcon style={{ fontSize: 26 }} /> : <PlayArrowIcon style={{ fontSize: 26 }} />}
                  </IconButton>
                  <Typography style={{ color: '#ffffff', fontSize: '13px', fontWeight: 500 }}>
                    {fmtClipMmss(pb.currentTimeSec || 0)} / {fmtClipMmss((pb.durationMs || 0) / 1000)}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <Tooltip title="Chụp ảnh">
                    <span>
                      <IconButton onClick={handleScreenshotClick} style={{ color: '#ffffff' }} size="small" disabled={!pb.everPlayable}>
                        <CameraAltIcon style={{ fontSize: 20 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={pb.downloadTooltip}>
                    <span>
                      <IconButton onClick={pb.handleDownloadClick} style={{ color: '#ffffff' }} size="small" disabled={!pb.downloadEnabled}>
                        <GetAppIcon style={{ fontSize: 20 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Thoát toàn màn hình">
                    <IconButton onClick={handleExitFullscreen} style={{ color: '#ffffff' }} size="small">
                      <FullscreenExitIcon style={{ fontSize: 22 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Box className={classes.cardDetails}>
        <Box>
          <Typography className={classes.clipTitle} title={clip.fileName}>
            {clip.fileName}
          </Typography>
        </Box>

        <Box className={classes.clipSub}>
          <span>{fmtClipMmss(pb.currentTimeSec || 0)}</span>
          <span>{fmtClipMmss((pb.durationMs || 0) / 1000)}</span>
        </Box>

        <ClipTimeline classes={classes} currentTimeSec={pb.currentTimeSec} bufferedSec={pb.bufferedSec} durationMs={pb.durationMs} onSeek={pb.handleSeek} />

        {pb.error && (
          <Typography style={{ color: '#d32f2f', fontSize: '11px', fontWeight: 600, marginTop: '4px' }}>
            ⚠ {pb.error}
          </Typography>
        )}
        {!pb.error && pb.note && (
          <Typography style={{ color: '#0066cc', fontSize: '11px', fontWeight: 600, marginTop: '4px' }}>
            {pb.note}
          </Typography>
        )}
        {pb.transferComplete && pb.status !== 'error' && pb.bufferedSec < (pb.durationMs || 0) / 1000 - 2 && (
          <Typography style={{ color: '#8a6d00', fontSize: '11px', fontWeight: 600, marginTop: '4px' }}>
            đoạn tải dở — xem được {clipTimelineText(pb.bufferedSec, pb.durationMs).replace('Đã tải: ', '')}
          </Typography>
        )}

        <Box className={classes.cardControls}>
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title={pb.isPlaying ? 'Tạm dừng' : (pb.everPlayable ? 'Phát tiếp' : 'Bắt đầu xem')}>
              <span>
                <IconButton onClick={pb.handlePlayToggle} className={classes.iconBtn} size="small" disabled={noSlot || pb.status === 'requesting'}>
                  {pb.status === 'requesting' || pb.status === 'buffering' ? (
                    <CircularProgress size={18} style={{ color: '#0066cc' }} />
                  ) : pb.isPlaying ? (
                    <PauseIcon style={{ fontSize: 18 }} />
                  ) : (
                    <PlayArrowIcon style={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>

            {pb.transferBtnMode === 'sending' && (
              <Tooltip title="Hủy chờ phản hồi từ thiết bị">
                <Button
                  size="small"
                  style={{ color: '#d32f2f', fontSize: '11px', fontWeight: 700, padding: '2px 8px', minWidth: 0, textTransform: 'none' }}
                  onClick={pb.handleCancelSending}
                >
                  Hủy
                </Button>
              </Tooltip>
            )}
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Chụp ảnh">
              <span>
                <IconButton onClick={handleScreenshotClick} className={classes.iconBtn} size="small" disabled={!pb.everPlayable}>
                  <CameraAltIcon style={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title={pb.downloadTooltip}>
              <span>
                <IconButton onClick={pb.handleDownloadClick} className={classes.iconBtn} size="small" disabled={!pb.downloadEnabled}>
                  <GetAppIcon style={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Toàn màn hình">
              <span>
                <IconButton onClick={handleFullscreen} className={classes.iconBtn} size="small" disabled={!pb.everPlayable}>
                  <FullscreenIcon style={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

function PlaybackTabInner({ isSidebarOpen, isShowResult }) {
  const classes = useStyles();

  const vehicleSelected = useSelector(state => state.vodSlice?.vehicleSelected);
  const deviceSerial = vehicleSelected?.deviceSerial || vehicleSelected?.serial || '';

  const [sinceMs, setSinceMs] = useState(() => Date.now() - 24 * 3600 * 1000);
  const [untilMs, setUntilMs] = useState(() => Date.now());

  const { recordings, loading: listLoading, error: listError, refresh } = useRecordingList(isShowResult ? deviceSerial : '');

  // Warm the decode-tier probe as soon as the tab mounts (mirrors live
  // view's warmWasmDecoder prefetch) so the first card's first ack does not
  // wait on it.
  useEffect(() => { resolveDecodeTier(); }, []);

  useEffect(() => {
    if (isShowResult && vehicleSelected && deviceSerial) {
      const start = vehicleSelected.first_time ? vehicleSelected.first_time * 1000 : Date.now() - 24 * 3600 * 1000;
      const end = vehicleSelected.last_time ? vehicleSelected.last_time * 1000 : Date.now();

      setSinceMs(start);
      setUntilMs(end);

      refresh({ sinceMs: start, untilMs: end });
    }
  }, [isShowResult, vehicleSelected, deviceSerial, refresh]);

  const handleRefresh = useCallback(() => {
    refresh({ sinceMs, untilMs });
  }, [refresh, sinceMs, untilMs]);

  const clips = (recordings || []).map((ev, idx) => {
    const startTsMs = ev.tsUs / 1000;
    const duration = ev.durationMs ?? ((ev.preRollMs ?? 0) + (ev.postRollMs ?? 0));
    const timestamp = moment(startTsMs).format('HH:mm:ss');
    const hour = moment(startTsMs).hour();
    const cameraNum = cameraNumFromMask(ev.cameraMask);
    const cameraLabel = camLabelFromMask(ev.cameraMask);

    return {
      id: ev.slotId != null ? `${ev.slotId}_${ev.tsUs}` : `clip_${idx}`,
      slot_id: ev.slotId,
      ts_us: ev.tsUs,
      startTsMs,
      durationMs: duration,
      timestamp,
      hour,
      cameraNum,
      cameraLabel,
      fileSize: fmtDuration(duration),
      fileName: `Lưu trữ thiết bị - ${cameraLabel} - ${moment(startTsMs).format('YYYY-MM-DD HH:mm:ss')}`,
      type: ev.type || 'recording',
    };
  });

  const groupClipsByHour = (clipsList) => {
    const groups = {};
    clipsList.forEach(clip => {
      const h = clip.hour;
      if (!groups[h]) {
        groups[h] = [];
      }
      groups[h].push(clip);
    });
    return groups;
  };

  const groupedClips = groupClipsByHour(clips);
  const sortedHours = Object.keys(groupedClips).sort((a, b) => Number(a) - Number(b));

  if (listLoading) {
    return (
      <Box className={classes.root} display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" gap={2}>
        <CircularProgress style={{ color: '#0066cc' }} />
      </Box>
    );
  }

  if (!vehicleSelected || !isShowResult) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        style={{ height: '60vh', marginTop: '24px' }}
      >
        <Typography style={{ color: '#D9042F', fontSize: '16px', fontWeight: 500, textAlign: 'center' }}>
          Vui lòng chọn biển số xe để tìm kiếm video.
        </Typography>
      </Box>
    );
  }

  if (listError) {
    return (
      <Box className={classes.root} display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" gap={2}>
        <Typography style={{ color: '#d32f2f', fontSize: '15px', fontWeight: 600 }}>
          Lỗi kết nối thiết bị: {listError}
        </Typography>
        <IconButton onClick={handleRefresh} style={{ color: '#0066cc' }}>
          <RefreshIcon />
        </IconButton>
      </Box>
    );
  }

  if (clips.length === 0) {
    return (
      <Box className={classes.root} display="flex" justifyContent="center" alignItems="center" height="100%">
        <Typography style={{ color: '#d32f2f', fontSize: '15px', fontWeight: 500 }}>
          Không tìm thấy video nào ghi trên thẻ nhớ trong khoảng thời gian này.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {sortedHours.map(hour => {
        const clipsInHour = groupedClips[hour];
        return (
          <Box className={classes.hourSection} key={hour}>
            <Typography className={classes.hourHeader}>
              {`${hour.padStart(2, '0')}:00 - ${(Number(hour) + 1).toString().padStart(2, '0')}:00 (${clipsInHour.length} video)`}
            </Typography>
            <Grid container spacing={3}>
              {clipsInHour.map(clip => (
                <Grid item key={clip.id} lg={3} md={4} sm={6} xs={12}>
                  <ClipCard clip={clip} deviceSerial={deviceSerial} classes={classes} />
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * usePlaybackDeviceProvider's own state (uploadingCard, lastRequestedCardKey)
 * must NOT survive a vehicle switch — a stale "card X is uploading" from the
 * previous device would mislabel every card's preemption-cost tooltip on the
 * new one. Mounting the hook itself inside the `key`-ed subtree (not just the
 * Provider it feeds) is what actually resets it: a `key` change unmounts and
 * remounts this whole component, so a fresh `useState(null)` runs.
 */
function PlaybackDeviceProviderMount({ deviceSerial, children }) {
  const deviceCtx = usePlaybackDeviceProvider(deviceSerial);
  return (
    <PlaybackDeviceContext.Provider value={deviceCtx}>
      {children}
    </PlaybackDeviceContext.Provider>
  );
}

/** One PlaybackDeviceContext per device_serial, scoped to the vehicle
 * currently selected — see PlaybackDeviceProviderMount above. */
export default function PlaybackTab({ isSidebarOpen, isShowResult }) {
  const vehicleSelected = useSelector(state => state.vodSlice?.vehicleSelected);
  const deviceSerial = vehicleSelected?.deviceSerial || vehicleSelected?.serial || '';
  return (
    <PlaybackDeviceProviderMount deviceSerial={deviceSerial} key={deviceSerial}>
      <PlaybackTabInner isSidebarOpen={isSidebarOpen} isShowResult={isShowResult} />
    </PlaybackDeviceProviderMount>
  );
}
